import { useState, useCallback } from "react";
import { uploadToCloudinary, uploadMultipleToCloudinary } from "../utils/uploadToCloudinary";

const MAX_IMAGES = 5;

export const useCloudinaryUpload = (folder, message) => {
    const [uploadedThumb, setUploadedThumb] = useState(null);
    // Each item: { url: string, public_id: string }
    const [uploadedImages, setUploadedImages] = useState([]);
    const [imageFileList, setImageFileList] = useState([]);
    const [uploading, setUploading] = useState(false);

    // ─── Thumbnail ────────────────────────────────────────────────────────────
    const uploadThumbnail = useCallback(
        async (file) => {
            try {
                setUploading(true);
                message.loading({ content: "Uploading thumbnail…", key: "thumb", duration: 0 });

                const res = await uploadToCloudinary(file, folder);
                setUploadedThumb(res);

                message.success({ content: "Thumbnail uploaded", key: "thumb", duration: 2 });
            } catch {
                message.error({ content: "Thumbnail upload failed", key: "thumb" });
            } finally {
                setUploading(false);
            }
        },
        [folder, message]
    );

    const removeThumbnail = useCallback(() => {
        setUploadedThumb(null);
    }, []);

    // ─── Property images ──────────────────────────────────────────────────────
    const uploadImages = useCallback(
        async (files) => {


            const availableSlots = MAX_IMAGES - imageFileList.length;

            if (availableSlots <= 0) {
                message.error(`Maximum ${MAX_IMAGES} images allowed`);
                return;
            }

            const filesToUpload = files.slice(0, availableSlots);

            if (files.length > availableSlots) {
                message.warning(
                    `Only ${availableSlots} image${availableSlots > 1 ? "s" : ""} can be uploaded`
                );
            }

            try {
                setUploading(true);

                message.loading({
                    content: "Uploading images...",
                    key: "imgs",
                    duration: 0,
                });

                // ✅ Upload images
                const results = await uploadMultipleToCloudinary(
                    filesToUpload,
                    folder
                );

                // ✅ Update uploaded images state
                setUploadedImages((prev) => {

                    const updated = [...prev, ...results];

                    const unique = updated.filter(
                        (img, index, self) =>
                            index === self.findIndex(
                                (i) => i.public_id === img.public_id
                            )
                    );

                    // ✅ STRICT LIMIT 5
                    return unique.slice(0, 5);
                });

                setImageFileList((prev) => {
                    const newFiles = results.map((img, index) => ({
                        uid: `${img.public_id}-${index}`,
                        name: filesToUpload[index]?.name || "image.jpg",
                        status: "done",
                        url: img.url,
                        public_id: img.public_id,
                    }));

                    return [...prev, ...newFiles].slice(0, MAX_IMAGES);
                });

                message.success({
                    content: "Images uploaded successfully",
                    key: "imgs",
                    duration: 2,
                });

            } catch (err) {

                message.error({
                    content: "Image upload failed",
                    key: "imgs",
                });

            } finally {
                setUploading(false);
            }
        },
        [imageFileList, folder, message]
    );

    /**
     * Remove a property image from state only (no Cloudinary API call needed
     * for unsigned uploads — images are cleaned up server-side on listing delete).
     * If you DO want to delete from Cloudinary, pass `apiClient` and it will call
     * your backend delete endpoint.
     */
    const removeImage = useCallback(
        async (public_id, apiClient) => {
            // Optimistically remove from UI first
            setUploadedImages((prev) =>
                prev.filter((img) => img.public_id !== public_id)
            );

            setImageFileList((prev) =>
                prev.filter((file) => file.public_id !== public_id)
            );

            if (apiClient) {
                try {
                    await apiClient.delete("/delete-image", { data: { public_id } });
                } catch {
                    // If the server delete fails, re-query isn't critical for unsigned uploads,
                    // but you could restore the image here if you want strict consistency.
                    message.error("Failed to delete image from server");
                }
            }
        },
        [message]
    );

    const clearAll = useCallback(() => {
        setUploadedThumb(null);
        setUploadedImages([]);
        setImageFileList([]);
    }, []);

    return {
        uploadedThumb,
        uploadedImages,
        imageFileList,
        setImageFileList,
        uploading,
        uploadThumbnail,
        removeThumbnail,
        uploadImages,
        removeImage,
        clearAll,
        // Convenience derived values
        imageCount: uploadedImages.length,
        canAddMoreImages: uploadedImages.length < MAX_IMAGES,
        remainingSlots: MAX_IMAGES - uploadedImages.length,
    };
};